import React, { useState, useEffect, useRef } from 'react';
import { host } from '../script/variables';

/* ─────────────────────────────────────────
   COLOUR PALETTE  (one per payment method)
───────────────────────────────────────── */
const PALETTE = {
  upi:        { bg: '#6366f1', light: '#e0e7ff' },
  cash:       { bg: '#10b981', light: '#d1fae5' },
  cc:         { bg: '#f59e0b', light: '#fef3c7' },
  dc:         { bg: '#3b82f6', light: '#dbeafe' },
  netbanking: { bg: '#8b5cf6', light: '#ede9fe' },
  wallet:     { bg: '#ec4899', light: '#fce7f3' },
  emi:        { bg: '#14b8a6', light: '#ccfbf1' },
  paylater:   { bg: '#f97316', light: '#ffedd5' },
  default:    { bg: '#94a3b8', light: '#f1f5f9' }
};

const getColor = (method) =>
  PALETTE[method?.toLowerCase()] || PALETTE.default;

/* ─────────────────────────────────────────
   SVG DONUT  (pure, no library needed)
───────────────────────────────────────── */
const DonutChart = ({ data, size = 220, thickness = 48 }) => {
  const [hovered, setHovered] = useState(null);
  const r      = (size - thickness) / 2;
  const cx     = size / 2;
  const cy     = size / 2;
  const circum = 2 * Math.PI * r;

  let cumulative = 0;

  const slices = data.map((d, i) => {
    const frac   = d.percentage / 100;
    const dash   = frac * circum;
    const offset = circum - cumulative * circum;
    cumulative  += frac;
    return { ...d, dash, offset, index: i };
  });

  const active = hovered !== null ? slices[hovered] : null;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* background track */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={thickness}
        />
        {slices.map((s, i) => (
          <circle
            key={s.method}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={getColor(s.method).bg}
            strokeWidth={hovered === i ? thickness + 6 : thickness}
            strokeDasharray={`${s.dash} ${circum}`}
            strokeDashoffset={s.offset}
            strokeLinecap="butt"
            style={{
              transition: 'stroke-width 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
      </svg>

      {/* centre label */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none'
      }}>
        {active ? (
          <>
            <span style={{
              fontSize: 13,
              fontWeight: 700,
              color: getColor(active.method).bg,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '-0.02em'
            }}>
              {active.label}
            </span>
            <span style={{
              fontSize: 20,
              fontWeight: 800,
              color: '#1e293b',
              fontFamily: "'DM Mono', monospace"
            }}>
              {active.percentage}%
            </span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em' }}>
              TOTAL
            </span>
            <span style={{
              fontSize: 16,
              fontWeight: 800,
              color: '#1e293b',
              fontFamily: "'DM Mono', monospace"
            }}>
              {data.length} methods
            </span>
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const PaymentMethodsChart = ({ cafeid, startDate, endDate }) => {
  const [chartData, setChartData] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [view,      setView]      = useState('amount'); // 'amount' | 'count'
  const [error,     setError]     = useState(null);

  useEffect(() => {
    if (!cafeid) return;
    fetchChart();
  }, [cafeid, startDate, endDate]);

  const fetchChart = async () => {
    setLoading(true);
    setError(null);
    try {
      const token    = localStorage.getItem('partnertoken');
      const response = await fetch(`${host}/transtats/transactions/chart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify({ cafeid, startDate, endDate })
      });

      const data = await response.json();
      if (data.success) {
        setChartData(data.chart);
      } else {
        setError(data.message || 'Failed to load chart data');
      }
    } catch (err) {
      setError('Network error — could not load chart');
      console.error('Chart fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  const activeData = chartData
    ? (view === 'amount' ? chartData.byAmount : chartData.byCount)
    : [];

  /* ── LOADING ── */
  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.shimmerHeader} />
        <div style={{ display: 'flex', gap: 32, padding: '24px 28px', alignItems: 'center' }}>
          <div style={styles.shimmerCircle} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[70, 55, 85, 45].map((w, i) => (
              <div key={i} style={{ ...styles.shimmerBar, width: `${w}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── ERROR ── */
  if (error) {
    return (
      <div style={styles.card}>
        <div style={{ padding: 28, textAlign: 'center', color: '#ef4444' }}>
          <span style={{ fontSize: 28 }}>⚠️</span>
          <p style={{ marginTop: 8, fontSize: 14 }}>{error}</p>
        </div>
      </div>
    );
  }

  /* ── EMPTY ── */
  if (!chartData || activeData.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Payment Methods</h3>
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <span style={{ fontSize: 36 }}>🏦</span>
          <p style={{ marginTop: 12, fontSize: 14 }}>No payment data for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      {/* ── HEADER ── */}
      <div style={styles.cardHeader}>
        <div>
          <h3 style={styles.cardTitle}>Payment Methods</h3>
          <p style={styles.cardSubtitle}>
            {chartData.totalTransactions} successful transactions
            &nbsp;·&nbsp;
            {formatAmount(chartData.totalAmount)} total
          </p>
        </div>

        {/* Toggle */}
        <div style={styles.toggleGroup}>
          <button
            style={{ ...styles.toggleBtn, ...(view === 'amount' ? styles.toggleActive : {}) }}
            onClick={() => setView('amount')}
          >
            By Amount
          </button>
          <button
            style={{ ...styles.toggleBtn, ...(view === 'count' ? styles.toggleActive : {}) }}
            onClick={() => setView('count')}
          >
            By Count
          </button>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={styles.body}>
        {/* Donut */}
        <DonutChart data={activeData} />

        {/* Legend */}
        <div style={styles.legend}>
          {activeData.map((d) => {
            const col    = getColor(d.method);
            const value  = view === 'amount'
              ? formatAmount(d.amount)
              : `${d.count} txn${d.count !== 1 ? 's' : ''}`;
            const barW   = `${d.percentage}%`;

            return (
              <div key={d.method} style={styles.legendRow}>
                {/* label + value */}
                <div style={styles.legendTop}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ ...styles.dot, background: col.bg }} />
                    <span style={styles.legendLabel}>{d.label}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={styles.legendValue}>{value}</span>
                    <span style={styles.legendPct}> {d.percentage}%</span>
                  </div>
                </div>

                {/* mini bar */}
                <div style={styles.barTrack}>
                  <div style={{
                    ...styles.barFill,
                    width: barW,
                    background: col.bg,
                    opacity: 0.85
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const shimmer = {
  background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite'
};

const styles = {
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 8px 24px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    marginBottom: 24
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #f1f5f9'
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: '#1e293b',
    letterSpacing: '-0.02em'
  },
  cardSubtitle: {
    margin: '4px 0 0',
    fontSize: 12,
    color: '#94a3b8',
    fontWeight: 500
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    gap: 28,
    padding: '24px 28px',
    flexWrap: 'wrap'
  },
  legend: {
    flex: 1,
    minWidth: 180,
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  legendRow: { display: 'flex', flexDirection: 'column', gap: 5 },
  legendTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  dot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontSize: 13, fontWeight: 600, color: '#334155' },
  legendValue: { fontSize: 13, fontWeight: 700, color: '#1e293b', fontFamily: "'DM Mono', monospace" },
  legendPct:   { fontSize: 12, color: '#94a3b8', fontFamily: "'DM Mono', monospace" },
  barTrack: { height: 5, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' },
  barFill:  { height: '100%', borderRadius: 99, transition: 'width 0.6s cubic-bezier(.4,0,.2,1)' },
  toggleGroup: {
    display: 'flex',
    background: '#f8fafc',
    borderRadius: 8,
    padding: 3,
    gap: 2,
    border: '1px solid #e2e8f0'
  },
  toggleBtn: {
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 600,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    background: 'transparent',
    color: '#64748b',
    transition: 'all 0.15s ease'
  },
  toggleActive: {
    background: '#fff',
    color: '#1e293b',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  /* shimmer placeholders */
  shimmerHeader: { height: 64, ...shimmer },
  shimmerCircle: { width: 220, height: 220, borderRadius: '50%', flexShrink: 0, ...shimmer },
  shimmerBar:    { height: 14, borderRadius: 8, ...shimmer }
};

/* inject keyframe once */
if (typeof document !== 'undefined' && !document.getElementById('chart-shimmer-kf')) {
  const s = document.createElement('style');
  s.id = 'chart-shimmer-kf';
  s.textContent = `@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`;
  document.head.appendChild(s);
}

export default PaymentMethodsChart;